import { useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";

import "./stylesStaffManagement.css";

import { AGENT_ACTIONS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";
import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
  handleExpiredTokenError,
} from "../../utils/ErrorUtils";

import { PlusOutlined } from "@ant-design/icons";

import Staff from "./Staff/Staff";
import Roles from "./Roles/Roles";
import NewStaff from "./NewStaff/NewStaff";
import AppContext from "../../context/AppContext";
import { STAFF_MANAGEMENT_STAFF_ID_QUERY_KEY } from "../../utils/NavigationUtils";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";

const tabs = [
  {
    label: AppStrings.STAFF,
    value: "staff",
  },
  {
    label: AppStrings.ROLES,
    value: "roles",
  },
  // {
  //   label: AppStrings.ACTIVITY,
  //   value: "activity",
  // },
];

function StaffManagement() {
  usePageTitle(AppStrings.STAFF_MANAGEMENT);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const staffIdFromUrl = useMemo(
    () => searchParams.get(STAFF_MANAGEMENT_STAFF_ID_QUERY_KEY)?.trim() || null,
    [searchParams]
  );
  const token = JSON.parse(localStorage.getItem('token'));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);
  
  const { adminActions } = useContext(AppContext);
  
  const [activeTab, setActiveTab] = useState("staff");
  const [isNewStaffOpen, setIsNewStaffOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isApiSending, setIsApiSending] = useState(false);
  const canCreatePAMUser = adminActions?.includes(AGENT_ACTIONS.CREATE_PAM_USER);

  const handleNewStaffClick = () => {
    setIsNewStaffOpen(true);
  };

  const handleCloseNewStaff = () => {
    setIsNewStaffOpen(false);
  };

  const handleSaveNewStaff = async (staffData) => {
    setIsApiSending(true);

    const params = {
      email: staffData.email,
      timezone: staffData.timezone || "Etc/UTC",
      first_name: staffData.firstName,
      last_name: staffData.lastName,
      role_id: staffData.role,
      ...(staffData.agentId?.trim() ? { agent_id: staffData.agentId.trim() } : {}),
      ...(staffData.position?.trim() ? { position: staffData.position.trim() } : {}),
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.CREATE_STAFF,
        params
      );
      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      setIsNewStaffOpen(false);
    } catch (error) {
      console.error(error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
      );
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsApiSending(false);
    }
  };

  return (
    <>
      <div className="staff-management-container">
        {isNewStaffOpen ? (
          <NewStaff
            onClose={handleCloseNewStaff}
            onSave={handleSaveNewStaff}
            isApiSending={isApiSending}
          />
        ) : (
          <>
            {!isDetailsOpen && (
              <>
                <div className="staff-management-header">
                  <div className="staff-management-header-buttons">
                    <button onClick={handleNewStaffClick} disabled={!canCreatePAMUser} className={!canCreatePAMUser ? "disabled" : ""}>
                      <PlusOutlined />
                      {AppStrings.NEW_STAFF}
                    </button>
                  </div>
                </div>
                <div className="staff-management-header-tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      className={tab.value === activeTab ? "active" : ""}
                      onClick={() => setActiveTab(tab.value)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="staff-management-body">
              {activeTab === "staff" && (
                <Staff
                  onDetailsOpenChange={setIsDetailsOpen}
                  initialStaffId={staffIdFromUrl}
                />
              )}
              {activeTab === "roles" && <Roles />}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default StaffManagement;
